import type { ThreadId } from "@schema/catalog.ts";
import { useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { setFilters, useFilters } from "@/state/filters.ts";
import styles from "./controls.module.css";

/**
 * Narrative threads, ordered by how much they matter to Doomsday
 * (`Thread.targetWeight`) rather than alphabetically — the multiverse and
 * Doom belong at the top of this list, always. Counts and OR semantics work
 * exactly like the character chips.
 */
export function ThreadFilter() {
  const catalog = useCatalog();
  const filters = useFilters();
  const { threadCounts } = useFilteredRoad();

  const selected = useMemo(() => new Set(filters.threads), [filters.threads]);

  const chips = useMemo(
    () =>
      catalog.threads
        .map((thread) => ({
          thread,
          count: threadCounts.get(thread.id) ?? 0,
          selected: selected.has(thread.id),
        }))
        .filter((chip) => chip.count > 0 || chip.selected)
        .sort(
          (a, b) =>
            b.thread.targetWeight - a.thread.targetWeight ||
            b.count - a.count ||
            a.thread.title.localeCompare(b.thread.title),
        ),
    [catalog.threads, threadCounts, selected],
  );

  const toggle = (id: ThreadId) =>
    setFilters((prev) => ({
      ...prev,
      threads: prev.threads.includes(id)
        ? prev.threads.filter((other) => other !== id)
        : [...prev.threads, id],
    }));

  return (
    <div className={styles.field}>
      <div className={styles.head}>
        <span className={styles.label} id="thread-filter-label">
          Threads
        </span>
        {filters.threads.length > 0 && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => setFilters((prev) => ({ ...prev, threads: [] }))}
          >
            {`Clear ${filters.threads.length}`}
          </button>
        )}
      </div>
      <ul className={styles.chips} aria-labelledby="thread-filter-label">
        {chips.map(({ thread, count, selected: on }) => (
          <li key={thread.id}>
            <button
              type="button"
              className={`${styles.chip} ${on ? styles.chipOn : ""}`}
              aria-pressed={on}
              onClick={() => toggle(thread.id)}
              title={thread.description}
            >
              {thread.title}
              <span className={styles.chipCount}>{count}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
